import { useThreadRuntime } from "@assistant-ui/react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Comment01Icon,
} from "@hugeicons/core-free-icons"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { formatDate } from "@/lib/utils/date"
import { useChatPanel } from "../hooks/use-chat-panel"
import { useThreadList } from "../queries/assistant-queries"
import { getIssue } from "../api/assistant-api"
import { toThreadMessages } from "../utils/to-thread-messages"
import type {
  Issue,
  IssueDraftListItem,
  IssueStatus,
} from "../types/assistant.types"

const STATUS_CONFIG: Record<IssueStatus, { label: string; chip: string }> = {
  DRAFT: { label: "In progress", chip: "bg-sky-50 text-sky-700" },
  OPEN: { label: "Submitted", chip: "bg-emerald-50 text-emerald-700" },
  IN_REVIEW: { label: "In review", chip: "bg-amber-50 text-amber-700" },
  RESOLVED: { label: "Resolved", chip: "bg-emerald-50 text-emerald-700" },
  ABANDONED: { label: "Discarded", chip: "bg-gray-100 text-gray-600" },
}

export function ThreadHistoryPanel() {
  const threadRuntime = useThreadRuntime()
  const setView = useChatPanel((s) => s.setView)
  const setThreadId = useChatPanel((s) => s.setThreadId)
  const setThreadStatus = useChatPanel((s) => s.setThreadStatus)
  const markAssistantReplied = useChatPanel((s) => s.markAssistantReplied)
  const { data, isLoading, isError, refetch } = useThreadList()

  const items = data ?? []

  const handleBack = () => setView("chat")

  const handleOpen = async (row: IssueDraftListItem) => {
    try {
      const full = await getIssue(row.id)
      loadIssueIntoRuntime(full)
    } catch {
      toast.error("Couldn't load that conversation.")
    }
  }

  const loadIssueIntoRuntime = (issue: Issue) => {
    threadRuntime.reset(toThreadMessages(issue.conversation_transcript))
    setThreadId(issue.id)
    setThreadStatus(issue.status)
    if (issue.conversation_transcript.some((m) => m.role === "assistant")) {
      markAssistantReplied()
    }
    setView("chat")
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 cursor-pointer"
          onClick={handleBack}
          title="Back to chat"
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
        </Button>
        <h3 className="text-sm font-semibold">Your conversations</h3>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : isError ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Couldn't load history.
            <Button
              variant="link"
              size="sm"
              className="ml-1 h-auto p-0 text-xs"
              onClick={() => refetch()}
            >
              Try again
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              <HugeiconsIcon
                icon={Comment01Icon}
                size={18}
                className="text-muted-foreground"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              No conversations yet.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {items.map((t) => (
              <HistoryRow key={t.id} row={t} onOpen={() => handleOpen(t)} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function HistoryRow({
  row,
  onOpen,
}: {
  row: IssueDraftListItem
  onOpen: () => void
}) {
  const status = STATUS_CONFIG[row.status] ?? STATUS_CONFIG.DRAFT
  const previewText =
    row.preview ||
    (row.status === "OPEN" || row.status === "IN_REVIEW" || row.status === "RESOLVED"
      ? row.summary || "Issue submitted."
      : "New conversation")

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full flex-col gap-1.5 rounded-lg border bg-background px-3 py-2.5 text-left ring-1 ring-border transition-all hover:bg-muted/30 hover:ring-primary/40 hover:shadow-sm cursor-pointer"
    >
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${status.chip}`}
        >
          {status.label}
        </span>
        {row.reference && (
          <span className="font-mono text-[11px] text-muted-foreground">
            {row.reference}
          </span>
        )}
        <span className="ml-auto text-[11px] text-muted-foreground">
          {formatDate(row.updated_at)}
        </span>
      </div>
      <p className="line-clamp-2 text-xs text-foreground">{previewText}</p>
      <div className="flex items-center justify-end text-muted-foreground group-hover:text-primary">
        <HugeiconsIcon icon={ArrowRight01Icon} size={12} />
      </div>
    </button>
  )
}
